import React, { type CSSProperties } from 'react';
import {
    ArrowRight,
    BriefcaseBusiness,
    FolderOpen,
    Home,
    MessageCircle,
    Phone,
    Search,
    UsersRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
    notFoundPageIconTokens,
    notFoundPageTokens,
    type NotFoundPageColorToken,
    type NotFoundPageIconToken,
} from './NotFoundPage.tokens';

export type { NotFoundPageColorToken, NotFoundPageIconToken } from './NotFoundPage.tokens';
export { notFoundPageIconTokens, notFoundPageTokens } from './NotFoundPage.tokens';

export type NotFoundPageLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    style?: CSSProperties;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type NotFoundPageIcon = NotFoundPageIconToken | React.ReactNode | false;

export type NotFoundPageColors = Partial<Record<NotFoundPageColorToken, string>>;

export type NotFoundPageSearch = {
    placeholder?: string;
    action?: string;
    method?: 'get' | 'post';
    name?: string;
    value?: string;
    ariaLabel?: string;
    buttonLabel?: string;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
};

export type NotFoundPageActionVariant = 'solid' | 'outline' | 'ghost';

export type NotFoundPageAction = {
    label?: React.ReactNode;
    href?: string;
    icon?: NotFoundPageIcon;
    iconPosition?: 'start' | 'end';
    variant?: NotFoundPageActionVariant;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
};

export type NotFoundPageBreadcrumb = {
    label?: React.ReactNode;
    href?: string;
    icon?: NotFoundPageIcon;
    ariaLabel?: string;
    title?: string;
};

export type NotFoundPageSuggestion = {
    title?: React.ReactNode;
    description?: React.ReactNode;
    href?: string;
    ctaLabel?: React.ReactNode;
    icon?: NotFoundPageIcon;
    accentColor?: string;
    accentColour?: string;
    ariaLabel?: string;
    titleAttribute?: string;
    target?: string;
    rel?: string;
};

export type NotFoundPageArtworkColors = {
    accent?: string;
    accentDark?: string;
    accentSoft?: string;
    background?: string;
    browser?: string;
    browserBorder?: string;
    browserBar?: string;
    browserDot?: string;
    document?: string;
    documentShadow?: string;
    fold?: string;
    face?: string;
    line?: string;
    plant?: string;
    plantStem?: string;
    cone?: string;
    coneStripe?: string;
    path?: string;
    plane?: string;
    ground?: string;
};

export type NotFoundPageArtworkProps = {
    colors?: NotFoundPageArtworkColors;
    colours?: NotFoundPageArtworkColors;
    accentColor?: string;
    accentColour?: string;
    title?: string;
    decorative?: boolean;
    className?: string;
    style?: CSSProperties;
};

export type NotFoundPageProps = {
    Link?: NotFoundPageLinkComponent;
    statusCode?: React.ReactNode | false;
    title?: React.ReactNode | false;
    titleComponent?: 'h1' | 'h2' | 'h3';
    description?: React.ReactNode | false;
    breadcrumbs?: NotFoundPageBreadcrumb[] | false;
    showBreadcrumbs?: boolean;
    actions?: NotFoundPageAction[] | false;
    search?: NotFoundPageSearch | false;
    illustration?: React.ReactNode | false;
    illustrationColors?: NotFoundPageArtworkColors;
    illustrationColours?: NotFoundPageArtworkColors;
    showIllustration?: boolean;
    suggestionsTitle?: React.ReactNode | false;
    suggestions?: NotFoundPageSuggestion[] | false;
    colors?: NotFoundPageColors;
    colours?: NotFoundPageColors;
    accentColor?: string;
    accentColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    maxWidth?: number | string;
    id?: string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type CssVarStyle = CSSProperties & Record<`--not-found-${string}`, string | number | undefined>;

type IconComponent = LucideIcon;

const t = notFoundPageTokens;

const iconRegistry: Record<NotFoundPageIconToken, IconComponent> = {
    arrowRight: ArrowRight,
    briefcase: BriefcaseBusiness,
    folder: FolderOpen,
    home: Home,
    messageCircle: MessageCircle,
    phone: Phone,
    search: Search,
    users: UsersRound,
};

const DefaultLink: NotFoundPageLinkComponent = ({ href, children, ...props }) => (
    <a href={href} {...props}>
        {children}
    </a>
);

const defaultActions: NotFoundPageAction[] = [
    {
        label: 'Go Home',
        href: '/',
        icon: 'home',
        variant: 'solid',
    },
    {
        label: 'Contact Us',
        href: '/contact',
        icon: 'messageCircle',
        variant: 'outline',
    },
];

const defaultBreadcrumbs: NotFoundPageBreadcrumb[] = [
    { label: 'Home', href: '/', icon: 'home' },
    { label: '404 - Page Not Found' },
];

const defaultSuggestions: NotFoundPageSuggestion[] = [
    {
        title: 'Our Services',
        description: 'Explore how we can help your business grow.',
        href: '/services',
        ctaLabel: 'View Services',
        icon: 'briefcase',
        accentColor: '#1155d9',
    },
    {
        title: 'Recent Projects',
        description: "See the work we're proud of.",
        href: '/projects',
        ctaLabel: 'View Projects',
        icon: 'folder',
        accentColor: '#22a06b',
    },
    {
        title: 'About Us',
        description: 'Learn more about our team and values.',
        href: '/about',
        ctaLabel: 'About Us',
        icon: 'users',
        accentColor: '#f07822',
    },
    {
        title: 'Contact Us',
        description: "Get in touch, we're happy to help.",
        href: '/contact',
        ctaLabel: 'Contact Now',
        icon: 'phone',
        accentColor: '#7c4dff',
    },
];

const notFoundPageCss = `
.not-found-page {
  --not-found-accent: ${t.colors.accent};
  --not-found-accent-dark: ${t.colors.accentDark};
  --not-found-accent-soft: ${t.colors.accentSoft};
  --not-found-accent-wash: ${t.colors.accentWash};
  --not-found-accent-contrast: ${t.colors.accentContrast};
  --not-found-bg: ${t.colors.background};
  --not-found-surface: ${t.colors.surface};
  --not-found-text: ${t.colors.text};
  --not-found-text-soft: ${t.colors.textSoft};
  --not-found-text-muted: ${t.colors.textMuted};
  --not-found-border: ${t.colors.border};
  --not-found-divider: ${t.colors.divider};
  --not-found-shadow: ${t.colors.shadow};
  --not-found-illustration-bg: ${t.colors.illustrationBackground};
  --not-found-illustration-soft: ${t.colors.illustrationSoft};
  --not-found-max-width: ${t.sizes.maxWidth}px;
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at 86% 8%, var(--not-found-accent-wash), transparent 27%),
    linear-gradient(180deg, #ffffff 0%, var(--not-found-bg) 100%);
  color: var(--not-found-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}

.not-found-page,
.not-found-page * {
  box-sizing: border-box;
}

.not-found-page a {
  color: inherit;
  text-decoration: none;
}

.not-found-page-shell {
  width: min(100%, var(--not-found-max-width));
  margin: 0 auto;
  padding: 0 40px;
}

.not-found-page-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}

.not-found-page-search {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-width: 0;
  border: 1px solid var(--not-found-border);
  border-radius: ${t.radius.search}px;
  background: var(--not-found-surface);
  color: var(--not-found-text-muted);
  box-shadow: 0 1px 2px rgba(15, 35, 74, 0.04);
}

.not-found-page-search[data-size="hero"] {
  width: min(100%, 286px);
  min-height: 58px;
  padding: 0 12px 0 20px;
}

.not-found-page-search:focus-within {
  border-color: color-mix(in srgb, var(--not-found-accent) 52%, var(--not-found-border));
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--not-found-accent) 12%, transparent);
}

.not-found-page-search-input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--not-found-text);
  font: inherit;
  font-size: 15px;
  line-height: 1.2;
  font-weight: 650;
}

.not-found-page-search-input::placeholder {
  color: var(--not-found-text-muted);
  opacity: 1;
}

.not-found-page-search-button {
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--not-found-text-soft);
  cursor: pointer;
}

.not-found-page-search-button:hover,
.not-found-page-search-button:focus-visible {
  background: var(--not-found-accent-soft);
  color: var(--not-found-accent);
}

.not-found-page-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.not-found-page-main {
  padding: 30px 0 86px;
}

.not-found-page-breadcrumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  color: var(--not-found-text-soft);
  font-size: 15px;
  line-height: 1.3;
  font-weight: 800;
}

.not-found-page-breadcrumb-item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.not-found-page-breadcrumb-link {
  color: var(--not-found-accent);
}

.not-found-page-breadcrumb-current {
  color: var(--not-found-text-soft);
}

.not-found-page-breadcrumb-separator {
  color: var(--not-found-text-muted);
}

.not-found-page-hero {
  display: grid;
  grid-template-columns: minmax(0, 0.88fr) minmax(420px, 1fr);
  align-items: center;
  gap: 64px;
  min-height: 430px;
}

.not-found-page-copy {
  min-width: 0;
}

.not-found-page-code {
  margin: 0 0 12px;
  color: var(--not-found-accent);
  font-size: clamp(92px, 11vw, 156px);
  line-height: 0.92;
  font-weight: 950;
  letter-spacing: 0;
}

.not-found-page-title {
  max-width: 640px;
  margin: 0;
  color: var(--not-found-text);
  font-size: clamp(38px, 4.6vw, 56px);
  line-height: 1.06;
  font-weight: 950;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}

.not-found-page-description {
  max-width: 570px;
  margin: 24px 0 0;
  color: var(--not-found-text-soft);
  font-size: 19px;
  line-height: 1.55;
  font-weight: 600;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}

.not-found-page-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 28px;
}

.not-found-page-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 56px;
  min-width: 150px;
  padding: 0 24px;
  border: 1.5px solid transparent;
  border-radius: ${t.radius.button}px;
  font-size: 16px;
  line-height: 1.12;
  font-weight: 900;
  letter-spacing: 0;
  text-align: center;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.not-found-page-action:hover,
.not-found-page-action:focus-visible {
  transform: translateY(-1px);
}

.not-found-page-action:focus-visible,
.not-found-page-breadcrumb-link:focus-visible,
.not-found-page-suggestion-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--not-found-accent) 24%, transparent);
  outline-offset: 4px;
}

.not-found-page-action[data-variant="solid"] {
  border-color: var(--not-found-accent);
  background: var(--not-found-accent);
  color: var(--not-found-accent-contrast);
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.14);
}

.not-found-page-action[data-variant="solid"]:hover,
.not-found-page-action[data-variant="solid"]:focus-visible {
  border-color: var(--not-found-accent-dark);
  background: var(--not-found-accent-dark);
}

.not-found-page-action[data-variant="outline"] {
  border-color: color-mix(in srgb, var(--not-found-accent) 68%, var(--not-found-border));
  background: var(--not-found-surface);
  color: var(--not-found-accent);
}

.not-found-page-action[data-variant="outline"]:hover,
.not-found-page-action[data-variant="outline"]:focus-visible {
  background: var(--not-found-accent-soft);
}

.not-found-page-action[data-variant="ghost"] {
  min-width: 0;
  border-color: transparent;
  background: transparent;
  color: var(--not-found-accent);
}

.not-found-page-visual {
  position: relative;
  min-width: 0;
}

.not-found-page-artwork {
  display: block;
  width: min(100%, 650px);
  height: auto;
  margin-left: auto;
  overflow: visible;
}

.not-found-page-illustration {
  position: relative;
  width: min(100%, 650px);
  min-height: 430px;
  margin-left: auto;
  isolation: isolate;
}

.not-found-page-illustration-oval {
  position: absolute;
  right: 4%;
  bottom: 30px;
  left: 5%;
  z-index: 0;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--not-found-accent) 13%, transparent), transparent);
}

.not-found-page-illustration-backdrop {
  position: absolute;
  top: 18px;
  right: 4%;
  z-index: 0;
  width: 72%;
  height: 75%;
  border-radius: 52% 48% 50% 45%;
  background:
    radial-gradient(circle at 18% 30%, rgba(255, 255, 255, 0.9), transparent 22%),
    var(--not-found-illustration-bg);
  transform: rotate(-5deg);
}

.not-found-page-illustration-dot,
.not-found-page-illustration-dot::before,
.not-found-page-illustration-dot::after {
  position: absolute;
  display: block;
  border-radius: 999px;
  background: color-mix(in srgb, var(--not-found-accent) 9%, #ffffff);
  content: "";
}

.not-found-page-illustration-dot {
  top: 52px;
  left: 18%;
  width: 38px;
  height: 38px;
}

.not-found-page-illustration-dot::before {
  top: 58px;
  left: -88px;
  width: 70px;
  height: 46px;
}

.not-found-page-illustration-dot::after {
  top: 86px;
  left: -38px;
  width: 40px;
  height: 28px;
}

.not-found-page-flight-path {
  position: absolute;
  top: 92px;
  right: 11%;
  z-index: 1;
  width: 59%;
  height: 135px;
  border: 2px dashed color-mix(in srgb, var(--not-found-accent) 28%, transparent);
  border-bottom: 0;
  border-left: 0;
  border-radius: 0 100% 0 0;
  transform: rotate(-3deg);
}

.not-found-page-paper-plane {
  position: absolute;
  top: 15px;
  right: 9%;
  z-index: 3;
  width: 78px;
  height: 52px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--not-found-accent) 24%, #ffffff), var(--not-found-accent));
  clip-path: polygon(0 38%, 100% 0, 72% 100%, 55% 50%);
  transform: rotate(-12deg);
  opacity: 0.58;
}

.not-found-page-browser {
  position: absolute;
  top: 82px;
  right: 16%;
  z-index: 2;
  width: 405px;
  max-width: 68%;
  height: 318px;
  overflow: hidden;
  border: 4px solid color-mix(in srgb, var(--not-found-accent) 30%, #ffffff);
  border-radius: 12px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.93), rgba(255, 255, 255, 0.78)),
    color-mix(in srgb, var(--not-found-accent) 8%, #ffffff);
  box-shadow: 0 28px 60px rgba(17, 49, 110, 0.13);
}

.not-found-page-browser-bar {
  display: flex;
  height: 42px;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
  background: linear-gradient(90deg, color-mix(in srgb, var(--not-found-accent) 34%, #ffffff), color-mix(in srgb, var(--not-found-accent) 18%, #ffffff));
}

.not-found-page-browser-dot {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.86);
}

.not-found-page-document-shadow {
  position: absolute;
  right: 30%;
  bottom: 26px;
  width: 170px;
  height: 246px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--not-found-accent) 12%, transparent);
  transform: translate(26px, 10px);
}

.not-found-page-document {
  position: absolute;
  right: 28%;
  bottom: 34px;
  width: 176px;
  height: 252px;
  padding: 44px 28px 28px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 18px 34px rgba(17, 49, 110, 0.1);
}

.not-found-page-document::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 42px;
  height: 42px;
  border-radius: 0 12px 0 12px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0), color-mix(in srgb, var(--not-found-accent) 9%, #ffffff) 50%);
}

.not-found-page-face {
  position: relative;
  width: 78px;
  height: 78px;
  margin: 0 auto 24px;
  border: 5px solid color-mix(in srgb, var(--not-found-accent) 46%, #ffffff);
  border-radius: 999px;
}

.not-found-page-face-eye {
  position: absolute;
  top: 24px;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--not-found-accent) 46%, #ffffff);
}

.not-found-page-face-eye:first-child {
  left: 22px;
}

.not-found-page-face-eye:nth-child(2) {
  right: 22px;
}

.not-found-page-face-mouth {
  position: absolute;
  right: 18px;
  bottom: 15px;
  left: 18px;
  height: 20px;
  border-top: 5px solid color-mix(in srgb, var(--not-found-accent) 46%, #ffffff);
  border-radius: 999px 999px 0 0;
}

.not-found-page-document-line {
  display: block;
  height: 9px;
  margin-top: 11px;
  border-radius: 999px;
  background: #dbe3ef;
}

.not-found-page-document-line:nth-of-type(2) {
  width: 78%;
}

.not-found-page-document-line:nth-of-type(3) {
  width: 58%;
}

.not-found-page-plant {
  position: absolute;
  z-index: 1;
  bottom: 73px;
  width: 106px;
  height: 132px;
}

.not-found-page-plant[data-side="left"] {
  left: 10%;
}

.not-found-page-plant[data-side="right"] {
  right: 0;
  bottom: 75px;
  width: 146px;
  height: 180px;
}

.not-found-page-stem {
  position: absolute;
  right: 50%;
  bottom: 0;
  width: 3px;
  height: 100%;
  border-radius: 999px;
  background: rgba(54, 154, 134, 0.35);
  transform-origin: bottom;
}

.not-found-page-leaf {
  position: absolute;
  width: 34px;
  height: 64px;
  border-radius: 100% 0 100% 0;
  background: rgba(87, 188, 166, 0.34);
  transform-origin: bottom;
}

.not-found-page-leaf:nth-child(2) {
  right: 48%;
  bottom: 44px;
  transform: rotate(-34deg);
}

.not-found-page-leaf:nth-child(3) {
  left: 52%;
  bottom: 24px;
  transform: rotate(36deg);
}

.not-found-page-leaf:nth-child(4) {
  left: 55%;
  bottom: 88px;
  transform: rotate(54deg);
}

.not-found-page-cone {
  position: absolute;
  right: 15%;
  bottom: 35px;
  z-index: 3;
  width: 105px;
  height: 142px;
}

.not-found-page-cone-body {
  position: absolute;
  right: 20px;
  bottom: 12px;
  width: 64px;
  height: 126px;
  background: linear-gradient(90deg, color-mix(in srgb, var(--not-found-accent) 42%, #ffffff), var(--not-found-accent));
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  opacity: 0.82;
}

.not-found-page-cone-stripe {
  position: absolute;
  right: 20px;
  left: 20px;
  height: 18px;
  background: rgba(255, 255, 255, 0.82);
  transform: skewX(-10deg);
}

.not-found-page-cone-stripe[data-line="one"] {
  bottom: 68px;
}

.not-found-page-cone-stripe[data-line="two"] {
  bottom: 32px;
}

.not-found-page-cone-base {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 16px;
  border-radius: 999px;
  background: var(--not-found-accent);
  box-shadow: 0 7px 15px rgba(17, 49, 110, 0.16);
}

.not-found-page-suggestions {
  margin-top: 42px;
  padding: 30px 36px;
  border: 1px solid color-mix(in srgb, var(--not-found-border) 72%, transparent);
  border-radius: ${t.radius.panel}px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: var(--not-found-shadow);
}

.not-found-page-suggestions-title {
  margin: 0 0 24px;
  color: var(--not-found-text);
  font-size: 22px;
  line-height: 1.18;
  font-weight: 950;
  letter-spacing: 0;
}

.not-found-page-suggestion-list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
  padding: 0;
  list-style: none;
}

.not-found-page-suggestion-item {
  min-width: 0;
  border-left: 1px solid var(--not-found-divider);
}

.not-found-page-suggestion-item:first-child {
  border-left: 0;
}

.not-found-page-suggestion-link {
  --not-found-suggestion-accent: var(--not-found-accent);
  display: grid;
  grid-template-columns: 70px minmax(0, 1fr);
  gap: 18px;
  min-height: 106px;
  padding: 0 28px;
  color: var(--not-found-text);
}

.not-found-page-suggestion-link:hover .not-found-page-suggestion-cta,
.not-found-page-suggestion-link:focus-visible .not-found-page-suggestion-cta {
  gap: 10px;
}

.not-found-page-suggestion-icon {
  display: inline-flex;
  width: 64px;
  height: 64px;
  align-items: center;
  justify-content: center;
  border-radius: ${t.radius.icon}px;
  background: color-mix(in srgb, var(--not-found-suggestion-accent) 12%, #ffffff);
  color: var(--not-found-suggestion-accent);
}

.not-found-page-suggestion-title {
  margin: 0;
  color: var(--not-found-text);
  font-size: 16px;
  line-height: 1.25;
  font-weight: 950;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}

.not-found-page-suggestion-description {
  margin: 8px 0 0;
  color: var(--not-found-text-soft);
  font-size: 15px;
  line-height: 1.45;
  font-weight: 600;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}

.not-found-page-suggestion-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  color: var(--not-found-accent);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 950;
  letter-spacing: 0;
  transition: gap 160ms ease;
}

@media (max-width: 1160px) {
  .not-found-page-hero {
    grid-template-columns: minmax(0, 0.95fr) minmax(360px, 0.85fr);
    gap: 34px;
  }

  .not-found-page-suggestion-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 26px 0;
  }

  .not-found-page-suggestion-item:nth-child(odd) {
    border-left: 0;
  }
}

@media (max-width: 860px) {
  .not-found-page-shell {
    padding: 0 24px;
  }

  .not-found-page-main {
    padding-top: 22px;
  }

  .not-found-page-hero {
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
    min-height: 0;
  }

  .not-found-page-visual {
    order: -1;
  }

  .not-found-page-illustration {
    min-height: 300px;
    margin: 0 auto;
  }

  .not-found-page-artwork {
    width: min(100%, 560px);
    margin: 0 auto;
  }

  .not-found-page-browser {
    top: 52px;
    right: 15%;
    height: 232px;
  }

  .not-found-page-document {
    width: 132px;
    height: 186px;
    padding: 30px 20px 20px;
  }

  .not-found-page-document-shadow {
    width: 132px;
    height: 186px;
  }

  .not-found-page-face {
    width: 58px;
    height: 58px;
    border-width: 4px;
  }

  .not-found-page-face-eye {
    top: 18px;
    width: 7px;
    height: 7px;
  }

  .not-found-page-face-eye:first-child {
    left: 16px;
  }

  .not-found-page-face-eye:nth-child(2) {
    right: 16px;
  }

  .not-found-page-face-mouth {
    right: 13px;
    bottom: 10px;
    left: 13px;
    border-top-width: 4px;
  }

  .not-found-page-cone {
    right: 12%;
    width: 84px;
    height: 112px;
  }

  .not-found-page-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .not-found-page-search[data-size="hero"] {
    grid-column: 1 / -1;
    width: 100%;
  }
}

@media (max-width: 560px) {
  .not-found-page-shell {
    padding: 0 18px;
  }

  .not-found-page-breadcrumbs {
    font-size: 13px;
  }

  .not-found-page-visual {
    display: none;
  }

  .not-found-page-code {
    font-size: 88px;
  }

  .not-found-page-title {
    font-size: 34px;
  }

  .not-found-page-description {
    margin-top: 18px;
    font-size: 16px;
  }

  .not-found-page-actions {
    grid-template-columns: minmax(0, 1fr);
  }

  .not-found-page-action {
    width: 100%;
    min-width: 0;
  }

  .not-found-page-suggestions {
    margin-top: 34px;
    padding: 22px 18px;
  }

  .not-found-page-suggestion-list {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }

  .not-found-page-suggestion-item {
    border-left: 0;
    border-top: 1px solid var(--not-found-divider);
    padding-top: 20px;
  }

  .not-found-page-suggestion-item:first-child {
    border-top: 0;
    padding-top: 0;
  }

  .not-found-page-suggestion-link {
    grid-template-columns: 56px minmax(0, 1fr);
    gap: 14px;
    min-height: 0;
    padding: 0;
  }

  .not-found-page-suggestion-icon {
    width: 52px;
    height: 52px;
  }

}
`;

function getCssSize(value: number | string | undefined): string | undefined {
    if (typeof value === 'number') {
        return `${value}px`;
    }

    return value;
}

function isIconToken(icon: React.ReactNode): icon is NotFoundPageIconToken {
    return typeof icon === 'string' && (notFoundPageIconTokens as readonly string[]).includes(icon);
}

function hasContent(value: React.ReactNode | false | undefined): boolean {
    return value !== false && value !== null && value !== undefined && value !== '';
}

function safeRel(target?: string, rel?: string) {
    return target === '_blank' && !rel ? 'noopener noreferrer' : rel;
}

function renderIcon(icon: NotFoundPageIcon | undefined, size = 20, strokeWidth = 2.2) {
    if (!icon) {
        return null;
    }

    if (isIconToken(icon)) {
        const Icon = iconRegistry[icon];

        return Icon ? <Icon size={size} strokeWidth={strokeWidth} aria-hidden="true" /> : null;
    }

    return <>{icon}</>;
}

function LinkOrAnchor({
    Link,
    href,
    children,
    ...props
}: {
    Link: NotFoundPageLinkComponent;
    href?: string;
    children: React.ReactNode;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    style?: CSSProperties;
    'aria-label'?: string;
    [key: string]: any;
}) {
    if (!href) {
        return <span {...props}>{children}</span>;
    }

    return (
        <Link href={href} {...props}>
            {children}
        </Link>
    );
}

function SearchForm({ search }: { search: NotFoundPageSearch }) {
    const placeholder = search.placeholder || t.defaults.searchPlaceholder;
    const buttonLabel = search.buttonLabel || 'Search';

    return (
        <form
            className="not-found-page-search"
            data-size="hero"
            action={search.action}
            method={search.method || 'get'}
            role="search"
            aria-label={search.ariaLabel || placeholder}
            onSubmit={search.onSubmit}
        >
            <span className="not-found-page-icon" aria-hidden="true">
                <Search size={22} strokeWidth={2.3} />
            </span>
            <input
                className="not-found-page-search-input"
                type="search"
                name={search.name || 'q'}
                defaultValue={search.value}
                placeholder={placeholder}
                aria-label={placeholder}
            />
            <button className="not-found-page-search-button" type="submit">
                <span className="not-found-page-icon" aria-hidden="true">
                    <Search size={18} strokeWidth={2.4} />
                </span>
                <span className="not-found-page-sr-only">{buttonLabel}</span>
            </button>
        </form>
    );
}

function ActionLink({
    action,
    Link,
    className = 'not-found-page-action',
}: {
    action: NotFoundPageAction;
    Link: NotFoundPageLinkComponent;
    className?: string;
}) {
    if (!hasContent(action.label) && !action.icon) {
        return null;
    }

    const icon = renderIcon(action.icon, 21, 2.4);
    const iconPosition = action.iconPosition || 'start';
    const content = (
        <>
            {icon && iconPosition === 'start' ? (
                <span className="not-found-page-icon" aria-hidden="true">
                    {icon}
                </span>
            ) : null}
            {hasContent(action.label) ? <span>{action.label}</span> : null}
            {icon && iconPosition === 'end' ? (
                <span className="not-found-page-icon" aria-hidden="true">
                    {icon}
                </span>
            ) : null}
        </>
    );

    return (
        <LinkOrAnchor
            Link={Link}
            href={action.href}
            className={className}
            data-variant={action.variant || 'solid'}
            aria-label={action.ariaLabel}
            title={action.title}
            target={action.target}
            rel={safeRel(action.target, action.rel)}
        >
            {content}
        </LinkOrAnchor>
    );
}

const defaultArtworkColors: Required<NotFoundPageArtworkColors> = {
    accent: '#1155D9',
    accentDark: '#0B43B9',
    accentSoft: 'rgba(17, 85, 217, 0.16)',
    background: 'rgba(17, 85, 217, 0.08)',
    browser: 'rgba(255, 255, 255, 0.92)',
    browserBorder: '#B8CCF6',
    browserBar: '#C7D8FA',
    browserDot: 'rgba(255, 255, 255, 0.92)',
    document: 'rgba(255, 255, 255, 0.95)',
    documentShadow: 'rgba(17, 85, 217, 0.12)',
    fold: '#EEF3FB',
    face: '#8DAAEC',
    line: '#DBE3EF',
    plant: 'rgba(87, 188, 166, 0.34)',
    plantStem: 'rgba(54, 154, 134, 0.42)',
    cone: '#1155D9',
    coneStripe: 'rgba(255, 255, 255, 0.84)',
    path: 'rgba(17, 85, 217, 0.28)',
    plane: '#7FA3EE',
    ground: 'rgba(17, 85, 217, 0.13)',
};

export function NotFoundPageArtwork({
    colors,
    colours,
    accentColor,
    accentColour,
    title = 'Page not found illustration',
    decorative = true,
    className = '',
    style,
}: NotFoundPageArtworkProps) {
    const mergedColors = {
        ...defaultArtworkColors,
        ...colours,
        ...colors,
    };
    const accent = accentColor || accentColour || mergedColors.accent;
    const c = {
        ...mergedColors,
        accent,
        accentDark: mergedColors.accentDark || accent,
        cone: mergedColors.cone || accent,
    };
    const hidden = decorative || !title;

    return (
        <svg
            className={`not-found-page-artwork ${className}`.trim()}
            viewBox="0 0 720 460"
            fill="none"
            role={hidden ? undefined : 'img'}
            aria-hidden={hidden ? true : undefined}
            aria-label={hidden ? undefined : title}
            style={style}
            xmlns="http://www.w3.org/2000/svg"
        >
            {!hidden ? <title>{title}</title> : null}

            <ellipse cx="365" cy="398" rx="302" ry="42" fill={c.ground} />
            <path
                d="M300 96C360 34 468 14 560 56C642 94 668 178 636 251C603 328 496 352 397 352C302 352 225 329 220 269C216 215 246 151 300 96Z"
                fill={c.background}
            />
            <circle cx="168" cy="83" r="20" fill={c.accentSoft} />
            <ellipse cx="86" cy="152" rx="38" ry="25" fill={c.accentSoft} />
            <ellipse cx="143" cy="176" rx="22" ry="14" fill={c.accentSoft} />

            <path
                d="M190 244C233 199 309 207 394 207C510 207 642 169 648 91"
                stroke={c.path}
                strokeWidth="3"
                strokeDasharray="8 10"
                strokeLinecap="round"
            />
            <path d="M604 45L676 20L642 88L630 55L604 45Z" fill={c.plane} opacity="0.82" />
            <path d="M630 55L676 20L648 61Z" fill={c.accentDark} opacity="0.2" />

            <g transform="translate(90 252)">
                <path
                    d="M47 116C43 78 35 47 17 14"
                    stroke={c.plantStem}
                    strokeWidth="4"
                    strokeLinecap="round"
                />
                <path d="M31 72C8 61-4 42 2 20C26 29 42 48 31 72Z" fill={c.plant} />
                <path d="M49 95C72 82 81 60 72 40C51 52 38 74 49 95Z" fill={c.plant} />
                <path d="M39 49C62 34 69 14 59 0C39 11 28 31 39 49Z" fill={c.plant} />
            </g>

            <g transform="translate(590 205) scale(1.22)">
                <path
                    d="M40 143C42 94 53 51 82 7"
                    stroke={c.plantStem}
                    strokeWidth="4"
                    strokeLinecap="round"
                />
                <path d="M53 102C22 92 4 68 7 41C39 49 60 74 53 102Z" fill={c.plant} />
                <path d="M56 75C87 59 104 32 99 6C66 17 47 45 56 75Z" fill={c.plant} />
                <path d="M37 128C8 131-11 117-18 92C12 88 34 102 37 128Z" fill={c.plant} />
            </g>

            <g>
                <rect
                    x="246"
                    y="92"
                    width="392"
                    height="280"
                    rx="14"
                    fill={c.browser}
                    stroke={c.browserBorder}
                    strokeWidth="5"
                />
                <path
                    d="M260 92H624C632 92 638 98 638 106V134H246V106C246 98 252 92 260 92Z"
                    fill={c.browserBar}
                />
                <circle cx="273" cy="114" r="7" fill={c.browserDot} />
                <circle cx="296" cy="114" r="7" fill={c.browserDot} />
                <circle cx="319" cy="114" r="7" fill={c.browserDot} />
                <rect
                    x="348"
                    y="177"
                    width="166"
                    height="204"
                    rx="12"
                    fill={c.documentShadow}
                    transform="rotate(-2 348 177)"
                />
                <path
                    d="M366 148H500L542 190V330C542 338 536 344 528 344H366C358 344 352 338 352 330V162C352 154 358 148 366 148Z"
                    fill={c.document}
                />
                <path d="M500 148V188C500 190 502 192 504 192H542L500 148Z" fill={c.fold} />
                <circle cx="445" cy="230" r="42" fill="none" stroke={c.face} strokeWidth="6" />
                <circle cx="429" cy="218" r="5.5" fill={c.face} />
                <circle cx="461" cy="218" r="5.5" fill={c.face} />
                <path
                    d="M425 255C434 243 455 243 465 255"
                    stroke={c.face}
                    strokeWidth="6"
                    strokeLinecap="round"
                />
                <rect x="388" y="284" width="118" height="9" rx="4.5" fill={c.line} />
                <rect x="388" y="306" width="118" height="9" rx="4.5" fill={c.line} />
                <rect x="388" y="328" width="76" height="9" rx="4.5" fill={c.line} />
            </g>

            <g transform="translate(526 284)">
                <path d="M64 0L108 133H20L64 0Z" fill={c.cone} opacity="0.86" />
                <path d="M64 0L86 133H20L64 0Z" fill="#ffffff" opacity="0.18" />
                <path d="M41 56H86L93 78H34L41 56Z" fill={c.coneStripe} />
                <path d="M29 94H100L106 116H23L29 94Z" fill={c.coneStripe} />
                <rect x="0" y="126" width="130" height="16" rx="8" fill={c.accentDark} />
            </g>
        </svg>
    );
}

const NotFoundPage = ({
    Link = DefaultLink,
    statusCode,
    title,
    titleComponent: TitleTag = 'h1',
    description,
    breadcrumbs,
    showBreadcrumbs = true,
    actions,
    search,
    illustration,
    illustrationColors,
    illustrationColours,
    showIllustration = true,
    suggestionsTitle,
    suggestions,
    colors,
    colours,
    accentColor,
    accentColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    maxWidth,
    id,
    className = '',
    style,
    ariaLabel = 'Page not found',
}: NotFoundPageProps) => {
    const colorOverrides = {
        ...colours,
        ...colors,
    };
    const mergedColors = {
        ...t.colors,
        ...colorOverrides,
    };
    const accent = accentColor || accentColour || colorOverrides.accent || mergedColors.accent;
    const resolvedStatusCode = statusCode === undefined ? t.defaults.statusCode : statusCode;
    const resolvedTitle = title === undefined ? t.defaults.title : title;
    const resolvedDescription = description === undefined ? t.defaults.description : description;
    const resolvedBreadcrumbs = breadcrumbs === undefined ? defaultBreadcrumbs : breadcrumbs;
    const resolvedActions = actions === undefined ? defaultActions : actions;
    const resolvedSearch =
        search === undefined ? { placeholder: t.defaults.searchPlaceholder } : search;
    const resolvedSuggestionsTitle =
        suggestionsTitle === undefined ? t.defaults.suggestionsTitle : suggestionsTitle;
    const resolvedSuggestions = suggestions === undefined ? defaultSuggestions : suggestions;
    const resolvedIllustrationColors = {
        ...illustrationColours,
        ...illustrationColors,
    };

    const rootStyle: CssVarStyle = {
        '--not-found-accent': accent,
        '--not-found-accent-dark': colorOverrides.accentDark || accent,
        '--not-found-accent-soft':
            colorOverrides.accentSoft || `color-mix(in srgb, ${accent} 10%, transparent)`,
        '--not-found-accent-wash':
            colorOverrides.accentWash || `color-mix(in srgb, ${accent} 6%, transparent)`,
        '--not-found-accent-contrast': mergedColors.accentContrast,
        '--not-found-bg': backgroundColor || backgroundColour || mergedColors.background,
        '--not-found-surface': surfaceColor || surfaceColour || mergedColors.surface,
        '--not-found-text': textColor || textColour || mergedColors.text,
        '--not-found-text-soft': mergedColors.textSoft,
        '--not-found-text-muted': mergedColors.textMuted,
        '--not-found-border': mergedColors.border,
        '--not-found-divider': mergedColors.divider,
        '--not-found-shadow': mergedColors.shadow,
        '--not-found-illustration-bg':
            colorOverrides.illustrationBackground ||
            `color-mix(in srgb, ${accent} 8%, transparent)`,
        '--not-found-illustration-soft':
            colorOverrides.illustrationSoft || `color-mix(in srgb, ${accent} 16%, transparent)`,
        '--not-found-max-width': getCssSize(maxWidth),
        ...style,
    };

    const crumbItems = Array.isArray(resolvedBreadcrumbs)
        ? resolvedBreadcrumbs.filter((item) => hasContent(item.label) || item.icon)
        : [];
    const actionItems = Array.isArray(resolvedActions)
        ? resolvedActions.filter((item) => hasContent(item.label) || item.icon)
        : [];
    const suggestionItems = Array.isArray(resolvedSuggestions)
        ? resolvedSuggestions.filter(
              (item) => hasContent(item.title) || hasContent(item.description),
          )
        : [];

    const shouldShowBreadcrumbs =
        showBreadcrumbs && resolvedBreadcrumbs !== false && crumbItems.length > 0;
    const shouldShowIllustration =
        showIllustration && illustration !== false && illustration !== null;
    const shouldShowSuggestions = resolvedSuggestions !== false && suggestionItems.length > 0;

    return (
        <div
            data-component="not-found-page"
            className={`not-found-page ${className}`.trim()}
            id={id}
            style={rootStyle}
            aria-label={ariaLabel}
        >
            <style>{notFoundPageCss}</style>

            <main className="not-found-page-main">
                <div className="not-found-page-shell">
                    {shouldShowBreadcrumbs ? (
                        <nav className="not-found-page-breadcrumbs" aria-label="Breadcrumb">
                            {crumbItems.map((item, index) => {
                                const isLast = index === crumbItems.length - 1;
                                const icon = renderIcon(item.icon, 18, 2.4);

                                return (
                                    <React.Fragment key={`breadcrumb-${index}`}>
                                        <LinkOrAnchor
                                            Link={Link}
                                            href={!isLast ? item.href : undefined}
                                            className={
                                                isLast
                                                    ? 'not-found-page-breadcrumb-item not-found-page-breadcrumb-current'
                                                    : 'not-found-page-breadcrumb-item not-found-page-breadcrumb-link'
                                            }
                                            aria-label={item.ariaLabel}
                                            title={item.title}
                                        >
                                            {icon ? (
                                                <span
                                                    className="not-found-page-icon"
                                                    aria-hidden="true"
                                                >
                                                    {icon}
                                                </span>
                                            ) : null}
                                            {hasContent(item.label) ? (
                                                <span>{item.label}</span>
                                            ) : null}
                                        </LinkOrAnchor>
                                        {!isLast ? (
                                            <span
                                                className="not-found-page-breadcrumb-separator"
                                                aria-hidden="true"
                                            >
                                                /
                                            </span>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                        </nav>
                    ) : null}

                    <section className="not-found-page-hero">
                        <div className="not-found-page-copy">
                            {hasContent(resolvedStatusCode) ? (
                                <p className="not-found-page-code">{resolvedStatusCode}</p>
                            ) : null}

                            {hasContent(resolvedTitle) ? (
                                <TitleTag className="not-found-page-title">
                                    {resolvedTitle}
                                </TitleTag>
                            ) : null}

                            {hasContent(resolvedDescription) ? (
                                <p className="not-found-page-description">{resolvedDescription}</p>
                            ) : null}

                            {actionItems.length || resolvedSearch ? (
                                <div className="not-found-page-actions">
                                    {actionItems.map((action, index) => (
                                        <ActionLink
                                            action={action}
                                            Link={Link}
                                            key={`action-${index}`}
                                        />
                                    ))}

                                    {resolvedSearch ? <SearchForm search={resolvedSearch} /> : null}
                                </div>
                            ) : null}
                        </div>

                        {shouldShowIllustration ? (
                            <div className="not-found-page-visual">
                                {illustration === undefined ? (
                                    <NotFoundPageArtwork
                                        accentColor={accent}
                                        colors={resolvedIllustrationColors}
                                    />
                                ) : illustration ? (
                                    illustration
                                ) : null}
                            </div>
                        ) : null}
                    </section>

                    {shouldShowSuggestions ? (
                        <section
                            className="not-found-page-suggestions"
                            aria-label={
                                typeof resolvedSuggestionsTitle === 'string'
                                    ? resolvedSuggestionsTitle
                                    : 'Suggested pages'
                            }
                        >
                            {hasContent(resolvedSuggestionsTitle) ? (
                                <h2 className="not-found-page-suggestions-title">
                                    {resolvedSuggestionsTitle}
                                </h2>
                            ) : null}

                            <ul className="not-found-page-suggestion-list">
                                {suggestionItems.map((item, index) => {
                                    const itemAccent =
                                        item.accentColor || item.accentColour || accent;
                                    const itemStyle: CssVarStyle = {
                                        '--not-found-suggestion-accent': itemAccent,
                                    };
                                    const icon = renderIcon(item.icon, 30, 2.1);
                                    const content = (
                                        <>
                                            <span
                                                className="not-found-page-suggestion-icon"
                                                aria-hidden="true"
                                            >
                                                {icon || <ArrowRight size={26} strokeWidth={2.1} />}
                                            </span>

                                            <span>
                                                {hasContent(item.title) ? (
                                                    <strong className="not-found-page-suggestion-title">
                                                        {item.title}
                                                    </strong>
                                                ) : null}
                                                {hasContent(item.description) ? (
                                                    <span className="not-found-page-suggestion-description">
                                                        {item.description}
                                                    </span>
                                                ) : null}
                                                {item.href && hasContent(item.ctaLabel) ? (
                                                    <span className="not-found-page-suggestion-cta">
                                                        <span>{item.ctaLabel}</span>
                                                        <ArrowRight size={17} strokeWidth={2.6} />
                                                    </span>
                                                ) : null}
                                            </span>
                                        </>
                                    );

                                    return (
                                        <li className="not-found-page-suggestion-item" key={index}>
                                            <LinkOrAnchor
                                                Link={Link}
                                                href={item.href}
                                                className="not-found-page-suggestion-link"
                                                style={itemStyle}
                                                aria-label={item.ariaLabel}
                                                title={item.titleAttribute}
                                                target={item.target}
                                                rel={safeRel(item.target, item.rel)}
                                            >
                                                {content}
                                            </LinkOrAnchor>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    ) : null}
                </div>
            </main>
        </div>
    );
};

export default NotFoundPage;
